import { formatDistance } from 'date-fns'
import { capitalize } from 'lodash'
import { useQuery } from 'react-query'
import { animated, useTrail } from 'react-spring'

import { getCurrentSpaceId } from '@/components/App/selectors'
import { ImagePreview } from '@/components/ImagePreview'
import { useRoles } from '@/components/RolesContainer'
import UserProfile from '@/components/Segments/Builder/UserProfile'
import Image from '@/components/TailwindUI/Image'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import StatusIndicator from '@/components/TailwindUI/StatusIndicator'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import AppEditMenu from '@/pages/SpaceAppBuilder/components/AppEditMenu/AppEditMenu'
import NewTemplateVersionTag from '@/pages/SpaceAppBuilder/components/NewTemplateVersionTag/NewTemplateVersionTag'
import { getAgentDescription, getAgentInstructions } from '@/pages/SpaceAppBuilder/utilities/agentHelpers'
import {
  App,
  AppInstanceDataSource,
  FeatureStatus,
  Template,
  WorkgridCatalogDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import isAppOwner from '@/pages/SpaceAppBuilder/utilities/isAppOwner'
import { dataSourceV3CatalogQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'
import cn from '@/utilities/cn'
import isTemplateUpdateAvailable from '@/utilities/isTemplateUpdateAvailable'

import AppBuilderIcon from './AppBuilderIcon'

type AppHandlers = {
  onOpen?: (id: string) => void
  onEdit?: (app: App) => void
  onExport?: (id: string) => void
  onDelete?: (id: string) => void
  onViewLogs?: (id: string) => void
  onDeactivate?: (id: string) => void
  onUpdateApp?: (app: App, templateId: string | undefined) => void
}

export const GRID = 'grid'
export const LIST = 'list'

export type AppLayout = 'grid' | 'list'

type AppsProps = {
  apps: any[]
  showEditMenu?: boolean
  layout: AppLayout
} & AppHandlers

export default function Apps({
  apps = [],
  layout,
  showEditMenu = true,
  onOpen,
  onEdit,
  onExport,
  onDelete,
  onViewLogs,
  onDeactivate,
  onUpdateApp
}: AppsProps) {
  // Prefetching for the inevitable nav to the templates page
  const templatesQuery = useQuery<{ items: Template[] }>({
    queryKey: ['/v1/appbuilder/templates']
  })

  const currentSpaceId = useAppSelector(getCurrentSpaceId)

  const workgridDataSourcesQuery = useQuery<WorkgridCatalogDatasource[]>({
    ...dataSourceV3CatalogQueryOptions({ spaceId: currentSpaceId })
  })

  return (
    <ul
      className={cn(
        layout === GRID
          ? 'grid w-full list-none grid-cols-1 gap-x-4 gap-y-4 py-5 lg:grid-cols-2 xl:grid-cols-3'
          : 'mt-5 divide-y divide-zinc-100 bg-white'
      )}
    >
      {apps?.map(app => (
        <AppItem
          key={app.id}
          layout={layout}
          showEditMenu={showEditMenu}
          app={app}
          onOpen={onOpen}
          onEdit={onEdit}
          onExport={onExport}
          onDelete={onDelete}
          onViewLogs={onViewLogs}
          onDeactivate={onDeactivate}
          onUpdateApp={onUpdateApp}
          spaceId={currentSpaceId}
          workgridDataSources={workgridDataSourcesQuery.data || []}
          templates={templatesQuery.data?.items}
        />
      ))}
    </ul>
  )
}

type AppItemProps = {
  app: any
  layout: string
  showEditMenu: boolean
  spaceId: string
  workgridDataSources: WorkgridCatalogDatasource[]
  templates?: Template[]
} & AppHandlers

function AppItem({
  layout,
  showEditMenu,
  app,
  onOpen,
  onEdit,
  onExport,
  onDelete,
  onViewLogs,
  onDeactivate,
  onUpdateApp,
  spaceId,
  workgridDataSources,
  templates = []
}: AppItemProps) {
  const roles = useRoles()
  const currentUser = useAppSelector(getCurrentUser)

  const features = useFeatures()
  const agentsFlagOn = !!features[WORKSHOP_AGENTS]

  const hasEditPermissions = roles.hasWorkshopAppOwnerRole(spaceId) && isAppOwner(app, currentUser?.id)

  const appDetailsQuery = useQuery<App>({
    queryKey: [`/v1/appbuilder/spaces/:spaceId/instances/${app.id}`]
  })

  const appDetails = appDetailsQuery.data

  const status =
    appDetails?.features?.find((feature: any) => feature.status === FeatureStatus.ACTIVE)?.status || FeatureStatus.DRAFT

  const templateId = app.template?.templateId

  const isUpdateAvailable = isTemplateUpdateAvailable({ app, templates })

  const readOnly = false

  const dataSources =
    workgridDataSources?.filter(
      ds =>
        appDetails?.dataSources?.find(
          (appDs: AppInstanceDataSource) => ds.id === appDs.datasourceId || ds.id === appDs.key
        ) && ds?.iconUrl
    ) ?? []

  return layout === GRID ? (
    <div
      className="relative flex w-full cursor-pointer rounded-lg border bg-white p-3 text-left shadow ring-offset-2 hover:ring hover:ring-blue-300"
      // Since we are making an entire container clickable (which contains interactive elements),
      // this is an exception to the rule to not attach onClick handlers to non-interactive elements
      onClick={() => onOpen?.(app.id)}
      role="button"
      tabIndex={0}
      data-testid={`grid-${app.id}`}
    >
      <div className="flex w-full flex-col justify-between">
        <div className="flex w-full justify-between">
          <div className="flex w-full items-start space-x-3">
            <AppImage src={app.iconUrl} size="lg" appName={app.name} />
            <div className="flex w-full flex-col">
              <div className="flex w-full items-center">
                <div className="item-center flex w-full space-x-3">
                  <div className="text-lg font-bold line-clamp-1" title={app.name}>
                    {app.name}
                  </div>
                  <div className="flex items-center pr-2">
                    {isUpdateAvailable && templateId && (
                      <NewTemplateVersionTag templateId={templateId} appId={app.id} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-4">
                  <Status status={status} isLoading={appDetailsQuery.isLoading} />
                  {showEditMenu && (
                    <AppEditMenu
                      aria-label={`${app.name} Settings`}
                      onOpen={() => onOpen?.(app.id)}
                      onEdit={() => onEdit?.(app)}
                      onExport={() => onExport?.(app.id)}
                      onDelete={() => onDelete?.(app.id)}
                      onViewLogs={() => onViewLogs?.(app.id)}
                      onUpdateApp={() => onUpdateApp?.(app, templateId)}
                      onDeactivate={() => onDeactivate?.(app.id)}
                      readOnly={readOnly}
                      status={status}
                      hasUpdateAvailable={isUpdateAvailable && !!templateId}
                      hasEditPermissions={hasEditPermissions}
                    />
                  )}
                </div>
              </div>
              <p
                className="pt-1 text-sm text-zinc-500 line-clamp-2"
                title={agentsFlagOn ? getAgentDescription(app) : app?.description}
              >
                {agentsFlagOn ? getAgentDescription(app) : app?.description}
              </p>
              {agentsFlagOn && <p className="pt-1 text-sm text-zinc-500 line-clamp-2">{getAgentInstructions(app)}</p>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 pl-1 text-sm">
          <div className="flex w-full items-center space-x-2">
            <UserProfile userId={app.modifiedBy} enabled={true} />
            <div className="ml-1 text-xs text-zinc-500 line-clamp-1">
              updated {formatDistance(new Date(app.modifiedDate), new Date(), { addSuffix: true })}
            </div>
          </div>
          <DataSources dataSources={dataSources} loading={appDetailsQuery.isLoading} reverse={true} />
        </div>
      </div>
    </div>
  ) : (
    <li>
      <div
        className="relative flex w-full cursor-pointer items-center justify-between gap-x-6 px-4 py-4 text-left hover:bg-zinc-50 sm:px-6"
        // Since we are making an entire container clickable (which contains interactive elements),
        // this is an exception to the rule to not attach onClick handlers to non-interactive elements
        onClick={() => onOpen?.(app.id)}
        role="button"
        tabIndex={0}
        data-testid={`list-${app.id}`}
      >
        <div className="flex w-full min-w-0 gap-x-4 transition-all md:w-2/3">
          <AppImage src={app.iconUrl} size="sm" appName={app.name} />
          <div className="min-w-0 flex-auto">
            <div className="text-sm font-semibold leading-6 text-zinc-900">
              <span className="absolute inset-x-0 -top-px bottom-0" />
              <div className="flex space-x-1">
                <div className="text-lg font-bold line-clamp-1">{app.name}</div>
                <DataSources dataSources={dataSources} size="sm" loading={appDetailsQuery.isLoading} />
                <div className="flex items-center">
                  {isUpdateAvailable && templateId && <NewTemplateVersionTag templateId={templateId} appId={app.id} />}
                </div>
              </div>
            </div>
            <p
              className="mt-0.5 flex text-sm leading-5 text-zinc-500 line-clamp-1"
              title={agentsFlagOn ? getAgentDescription(app) : app?.description}
            >
              {agentsFlagOn ? getAgentDescription(app) : app?.description}
            </p>
            {agentsFlagOn && (
              <p className="mt-0.5 flex text-sm leading-5 text-zinc-500 line-clamp-1">{getAgentInstructions(app)}</p>
            )}
          </div>
        </div>
        <div className="flex w-fit items-center gap-x-4 transition-all md:w-1/3">
          <div className="hidden w-5/6 transition-all md:flex">
            <div className="hidden flex-wrap items-center gap-y-2 transition-all md:flex">
              <UserProfile userId={app.modifiedBy} enabled={true} />
              <div className="ml-1 text-xs text-zinc-500 line-clamp-1">
                updated {formatDistance(new Date(app.modifiedDate), new Date(), { addSuffix: true })}
              </div>
            </div>
          </div>
          <div className="mt-1 hidden w-1/6 flex-none items-center transition-all md:flex">
            <Status status={status} isLoading={appDetailsQuery.isLoading} />
          </div>
          {showEditMenu && (
            <AppEditMenu
              aria-label={`${app.name} Settings`}
              onOpen={() => onOpen?.(app.id)}
              onEdit={() => onEdit?.(app)}
              onExport={() => onExport?.(app.id)}
              onDelete={() => onDelete?.(app.id)}
              onViewLogs={() => onViewLogs?.(app.id)}
              onUpdateApp={() => onUpdateApp?.(app, templateId)}
              onDeactivate={() => onDeactivate?.(app.id)}
              readOnly={readOnly}
              status={status}
              hasUpdateAvailable={!!(isUpdateAvailable && templateId)}
              hasEditPermissions={hasEditPermissions}
            />
          )}
        </div>
      </div>
    </li>
  )
}

type DataSourcesProps = {
  dataSources: any
  size?: 'sm' | 'lg'
  loading?: boolean
  reverse?: boolean
}
function DataSources({ dataSources, size = 'lg', loading = false, reverse = false }: DataSourcesProps) {
  const dataSourceTrails = useTrail(dataSources.length, {
    from: { scale: 0 },
    to: { scale: 1 }
  })

  return (
    <div className={cn('flex items-center gap-1', { 'flex-row-reverse': reverse })}>
      {loading ? (
        <div className="fade-in fade-out">
          <LoadingSpinner
            className={cn('ml-1', {
              'h-5 w-5': size === 'sm',
              'h-7 w-7': size === 'lg'
            })}
          />
        </div>
      ) : (
        dataSourceTrails.map((props, i) => (
          <animated.div key={dataSources[i].id} style={{ ...props, transitionDuration: '75ms' }}>
            <ImagePreview
              className={cn('ml-1 rounded', {
                'h-5 w-5': size === 'sm',
                'h-7 w-7': size === 'lg'
              })}
              src={dataSources[i].iconUrl}
              useLoadingImage={false}
              title={dataSources[i].name}
              alt={`${dataSources[i].name} Icon`}
            />
          </animated.div>
        ))
      )}
    </div>
  )
}

type StatusProps = {
  status: string
  isLoading?: boolean
}
function Status({ status, isLoading = false }: StatusProps) {
  return (
    <StatusIndicator
      label={
        isLoading ? (
          <p className="animate-pulse text-zinc-400">Loading...</p>
        ) : (
          <p className="text-zinc-500">{capitalize(status)}</p>
        )
      }
      variant={status === FeatureStatus.ACTIVE ? 'success' : 'default'}
    />
  )
}

type AppImageProps = {
  size: 'sm' | 'lg'
  src: string
  appName: string
}
export function AppImage({ size, src, appName }: AppImageProps) {
  return (
    <Image
      loadingImage={
        <div className="animate-pulse rounded-lg bg-zinc-50">
          <div
            className={cn({
              'h-14 w-14': size === 'sm',
              'h-20 w-20': size === 'lg'
            })}
          />
        </div>
      }
      fallbackImage={
        <div>
          <AppBuilderIcon
            className={cn('rounded-lg', {
              'h-14 w-14': size === 'sm',
              'h-20 w-20': size === 'lg'
            })}
          />
        </div>
      }
      className={cn('rounded-lg', {
        'h-14 w-14 flex-none': size === 'sm',
        'h-20 w-20': size === 'lg'
      })}
      src={src}
      alt={`${appName} Icon`}
    />
  )
}
